import { GiftOutlined } from "@ant-design/icons";
import {
  Button,
  Empty,
  Image,
  Popover,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import axios from "axios";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import useDarkModeClass from "../../../hooks/useDarkModeClass";
import { refresh } from "../../../store/adminSlice";
import Breadcrumb from "../../common/breadcrumb";
import LoyaltyPointsForm from "../../loyalty/LoyaltyPointsForm";

const { Option } = Select;

const STATUS_OPTIONS = [
  "Requested",
  "Approved",
  "Rejected",
  "Received",
  "Refunded",
];

export default function ReturnLists() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [showLoyaltyAddModal, setShowLoyaltyAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDarkMode = useDarkModeClass();

  useEffect(() => {
    fetchReturns(1, pagination.pageSize);
    // eslint-disable-next-line
  }, []);

  const fetchReturns = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `/order/api/v1/admin/returns/?page=${page}&page_size=${pageSize}`,
      );
      setData(res.data.results || []);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: res.data.count || 0,
      }));
    } catch (err) {
      if (err.response?.status === 401 && Cookies.get("logss")) {
        dispatch(refresh()).then(() => fetchReturns(page, pageSize));
      } else if (err.response?.status === 401 && !Cookies.get("logss")) {
        navigate("/");
      } else {
        message.error(t("returns.fetchFailed", "Failed to fetch returns"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    setUpdatingId(id);
    try {
      await axios.patch(`/order/api/v1/admin/returns/${id}/update-status/`, {
        status,
      });
      setData((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status } : item)),
      );
      message.success(t("returns.statusUpdated", "Status updated"));
    } catch (err) {
      const apiMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        t("returns.statusUpdateFailed", "Failed to update status");
      message.error(apiMsg);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleTableChange = (paginationInfo) => {
    fetchReturns(paginationInfo.current, paginationInfo.pageSize);
  };

  const handleLoyaltySuccess = () => {
    message.success(
      t("returns.loyaltySuccess", "Loyalty points added successfully"),
    );
  };

  const handleAddLoyaltyPoints = (record) => {
    setSelectedUser(record);
    setShowLoyaltyAddModal(true);
  };

  const columns = [
    {
      title: "S/N",
      key: "serial",
      render: (text, record, index) =>
        pagination.pageSize * (pagination.current - 1) + index + 1,
    },
    {
      title: t("returns.columns.id", "Return ID"),
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("returns.columns.user", "User Email"),
      dataIndex: "user_email",
      key: "user_email",
    },
    {
      title: t("table.columns.orderId", "Order ID"),
      dataIndex: "order_id",
      key: "order_id",
    },
    {
      title: t("orderDetails.productName", "Product Name"),
      dataIndex: "product_name",
      key: "product_name",
      render: (text) => (
        <Popover
          content={text}
          title={t("orderDetails.productName", "Full Product Name")}
        >
          <span style={{ cursor: "pointer" }}>
            {text && text.length > 30 ? text.slice(0, 30) + "..." : text}
          </span>
        </Popover>
      ),
    },
    {
      title: t("table.columns.product", "Image"),
      dataIndex: "image",
      key: "image",
      render: (url) =>
        url ? (
          <Image
            width={50}
            src={`${process.env.REACT_APP_PUBLIC_MEDIA_URL_BASE}${url}`}
          />
        ) : null,
    },
    {
      title: t("returns.columns.category", "Category"),
      dataIndex: "return_category_display",
      key: "return_category_display",
      render: (text, record) => {
        const val = record.return_category_display || record.return_category;
        return val ? <Tag color="blue">{val}</Tag> : "-";
      },
    },
    {
      title: t("returns.columns.subreason", "Sub-reason"),
      dataIndex: "return_subreason_display",
      key: "return_subreason_display",
      render: (text, record) => {
        const val = record.return_subreason_display || record.return_subreason;
        if (!val) return "-";
        return (
          <Popover
            content={val}
            title={t("returns.columns.subreason", "Sub-reason")}
          >
            <span style={{ cursor: "pointer" }}>
              {val.length > 25 ? val.slice(0, 25) + "..." : val}
            </span>
          </Popover>
        );
      },
    },
    {
      title: t("returns.columns.reason", "Reason Details"),
      dataIndex: "reason",
      key: "reason",
      render: (text) => (
        <Popover
          content={text}
          title={t("returns.columns.reason", "Full Reason Details")}
        >
          <span style={{ cursor: "pointer" }}>
            {text && text.length > 25 ? text.slice(0, 25) + "..." : text}
          </span>
        </Popover>
      ),
    },
    {
      title: t("table.columns.trackingNumber", "Tracking Number"),
      dataIndex: "tracking_number",
      key: "tracking_number",
      render: (text) =>
        text ? (
          <Typography.Text copyable style={isDarkMode ? { color: "#fff" } : {}}>
            {text}
          </Typography.Text>
        ) : (
          "-"
        ),
    },
    {
      title: t("table.columns.createdAt", "Created At"),
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => (date ? new Date(date)?.toLocaleString() : "-"),
    },
    {
      title: t("table.columns.actions", "Actions"),
      key: "actions",
      render: (text, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Select
            value={record.status}
            style={{ width: 125 }}
            onChange={(value) => handleStatusChange(record.id, value)}
            loading={updatingId === record.id}
          >
            {STATUS_OPTIONS.map((status) => (
              <Option value={status} key={status}>
                {t(`returns.statusOptions.${status.toLowerCase()}`, status)}
              </Option>
            ))}
          </Select>

          <Button
            color="default"
            variant="solid"
            icon={<GiftOutlined />}
            onClick={() => handleAddLoyaltyPoints(record)}
            title={t("returns.addLoyaltyPoints", "Add Loyalty Points")}
            style={
              isDarkMode
                ? { background: "#ff8084", borderColor: "#ff8084" }
                : {}
            }
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <Breadcrumb
        title={t("returns.title", "Return List")}
        parent={t("menu.orders", "Orders")}
      />
      <Spin spinning={loading} tip={t("orderDetails.loading", "Loading...")}>
        {data.length === 0 && !loading ? (
          <Empty
            description={t("returns.noReturns", "No return orders found")}
            style={{ margin: "40px 0" }}
          />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={data}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
            className={isDarkMode ? "dark-ant-table" : ""}
          />
        )}
      </Spin>

      {/* Loyalty Points Add Modal */}
      <LoyaltyPointsForm
        visible={showLoyaltyAddModal}
        onCancel={() => {
          setShowLoyaltyAddModal(false);
          setSelectedUser(null);
        }}
        onSuccess={() => {
          setShowLoyaltyAddModal(false);
          setSelectedUser(null);
          handleLoyaltySuccess();
        }}
        userId={selectedUser?.user_id}
        userName={selectedUser?.user_email}
        action="add"
        isDarkMode={isDarkMode}
      />
    </>
  );
}
